/**
 * PATH: packages/bills/src/components/BillVendorSection.tsx
 * FILE_ID: BILLS-PKG-CMP-VENDOR-012
 * PURPOSE: "Vendor" section of the bill data panel — the canonical supplier
 *          picker, one-click create+link affordance, and the free-text
 *          name / email / ABN inputs.
 *
 * WHY: Keeps the linked-Supplier orchestration UI together and off
 *      BillDataPanel's render. All state/handlers stay in the parent; this is
 *      a props-in view. Extracted from BillDataPanel.tsx (P4-1 split) — no
 *      behaviour change.
 */

import { Plus } from 'lucide-react';
import { Button, Input } from '@quotech/ui';
import type { BillDetail, BillSupplierDetail, BillSupplierOption } from '../types';
import { Field, Section } from './BillFieldPrimitives';
import { SupplierPickerField } from './SupplierPickerField';
import type { SetBillField } from './bill-panel-field';

interface BillVendorSectionProps {
  merged: Partial<BillDetail>;
  supplierDetail: BillSupplierDetail | null;
  busy: boolean;
  status: BillDetail['status'];
  canCreateSupplier: boolean;
  supplierBusy: boolean;
  supplierError: string | null;
  onPickSupplier: (option: BillSupplierOption | null) => void;
  onCreateAndLink: () => void;
  setField: SetBillField;
}

export function BillVendorSection({
  merged,
  supplierDetail,
  busy,
  status,
  canCreateSupplier,
  supplierBusy,
  supplierError,
  onPickSupplier,
  onCreateAndLink,
  setField,
}: BillVendorSectionProps) {
  return (
    <Section title="Vendor">
      <SupplierPickerField
        selectedId={merged.supplier ?? null}
        detail={supplierDetail}
        disabled={busy || status === 'pushed'}
        onPick={onPickSupplier}
        vendor={{
          name: merged.vendor_name,
          email: merged.vendor_email,
          abn: merged.vendor_abn,
        }}
      />
      {/* One-click create+link — surfaced when nothing is linked yet so
          the operator doesn't have to open the picker and hunt for the
          buried "+ Create" row. */}
      {!merged.supplier &&
        canCreateSupplier &&
        (merged.vendor_name ?? '').trim() &&
        status !== 'pushed' && (
          <Button
            variant="outline"
            size="sm"
            onClick={onCreateAndLink}
            disabled={busy || supplierBusy}
            // Left-aligned icon row, NOT the default centered label: a
            // full-width centered span re-aligns itself the moment a long
            // vendor name truncates, so the text visibly jumped between
            // bills. Only the quoted name ellipsizes — the "Create & link …
            // as supplier" sentence always stays readable (the title attr
            // carries the untruncated name).
            className="w-full min-w-0 justify-start gap-1.5 text-xs font-normal text-muted-foreground hover:text-foreground"
            title={`Create “${(merged.vendor_name ?? '').trim()}” as a supplier and link it to the bill`}
          >
            <Plus className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {supplierBusy ? (
              'Creating…'
            ) : (
              <span className="flex min-w-0 items-center gap-1 whitespace-nowrap">
                Create &amp; link
                <span className="min-w-0 truncate font-medium text-foreground">
                  “{(merged.vendor_name ?? '').trim()}”
                </span>
                as supplier
              </span>
            )}
          </Button>
        )}
      {supplierError && (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1.5 text-[11px] text-destructive">
          {supplierError}
        </p>
      )}
      <Field label="Name">
        <Input
          data-bill-field="vendor_name"
          value={merged.vendor_name ?? ''}
          onChange={(e) => setField('vendor_name', e.target.value)}
        />
      </Field>
      <Field label="Email">
        <Input
          data-bill-field="vendor_email"
          value={merged.vendor_email ?? ''}
          onChange={(e) => setField('vendor_email', e.target.value)}
        />
      </Field>
      <Field label="ABN">
        <Input
          data-bill-field="vendor_abn"
          value={merged.vendor_abn ?? ''}
          onChange={(e) => setField('vendor_abn', e.target.value)}
        />
      </Field>
    </Section>
  );
}
